
import { Component, Ref } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router";
import { MetaInfo } from "vue-meta";
import { SearchClient } from "algoliasearch";
import { AlgoliaSearchHelper, SearchParameters } from "algoliasearch-helper";
import { history } from "instantsearch.js/es/lib/routers";
import { singleIndex } from "instantsearch.js/es/lib/stateMappings";
import { debounce } from "throttle-debounce";
import { Constants } from "@app/constants";
import CiSuggestedExternalContent from "@components/ads/suggested-external-content.vue";
import AISNumericMenu from "@components/base/ais/numeric-menu.vue";
import AISRangeWithPeriod from "@components/base/ais/range-with-period.vue";
import AISSearch from "@components/base/ais/search.vue";
import AISSelect from "@components/base/ais/select.vue";
import AISSfdrRefinementList from "@components/base/ais/sfdr-refinement-list.vue";
import AISToggle from "@components/base/ais/toggle.vue";
import CiEmptystate from "@components/base/emptystate.vue";
import CiRegistrationModal from "@components/base/registration-modal.vue";
import { BaseView } from "@components/base/view";
import ProductCollectionTable from "@components/products/collection-table.vue";
import ProductComparisonCart from "@components/products/comparison-cart.vue";
import { useLoader } from "@composables/loader";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { Period } from "@models/enums";
import { algoliaService } from "@services/algolia.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import { apiService } from "@services/api.service";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { settingsService } from "@services/settings.service";
import { Emitter } from "@utils/events";

type PeriodOptions = {
  performance: Period[];
  maximum_draw_down: Period[];
  volatility: Period[];
  sharpe_ratio: Period[];
  tracking_error: Period[];
  correlation_coefficient: Period[];
  information_ratio: Period[];
  beta: Period[];
  treynor_ratio: Period[];
};

type AisSelectOptionPayload = {
  count: number;
  isRefined: boolean;
  data: ExplicitAny;
  label: string;
  value: string;
};

@Component<any>({
  name: "ci-product-search",
  components: {
    "ci-ais-numeric-menu": AISNumericMenu,
    "ci-ais-range-with-period": AISRangeWithPeriod,
    "ci-ais-search": AISSearch,
    "ci-ais-select": AISSelect,
    "ci-ais-sfdr-refinement-list": AISSfdrRefinementList,
    "ci-ais-toggle": AISToggle,
    "ci-emptystate": CiEmptystate,
    "ci-partners": () => import("./partners.vue"),
    "ci-product-collection-table": ProductCollectionTable,
    "ci-product-comparison-cart": ProductComparisonCart,
    "ci-product-partner-funds-carousel": () => import("@components/products/product-partner-funds-carousel.vue"),
    "ci-registration-modal": CiRegistrationModal,
    "ci-sales-banner": () => import("@components/products/ranking/sales-banner.vue"),
    "ci-suggested-external-content": CiSuggestedExternalContent,
  },
  metaInfo(): MetaInfo {
    return {
      title: this.$t("products.search.meta_tags.title"),
      meta: [
        { vmid: "description", name: "description", content: this.$t("products.search.meta_tags.description") },
        { vmid: "og:title", property: "og:title", content: this.$t("products.search.meta_tags.title") },
        {
          vmid: "og:description",
          property: "og:description",
          content: this.$t("products.search.meta_tags.description"),
        },
        { vmid: "twitter:title", name: "twitter:title", content: this.$t("products.search.meta_tags.title") },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: this.$t("products.search.meta_tags.description"),
        },
      ],
    };
  },
})
export default class extends BaseView {
  @Ref("comparisonCart") comparisonCart!: any;
  @Ref("results") results!: HTMLElement;

  public rawIndexName: string = settingsService.options.algolia_index_names.funds;
  public indexName = `${this.rawIndexName}_perf_1y_desc`;
  public searchClient: SearchClient = algoliaService.getClient();
  public searchConfig: Indexable = {
    hitsPerPage: 10,
  };

  public resultsInViewport: boolean = false;
  public routing: Indexable = {
    router: history({ writeDelay: 600 }),
    stateMapping: singleIndex(this.indexName),
  };

  public showAllFilters = false;
  public showFilterGroups = {
    performance: false,
    investment_areas: false,
    fees: false,
    types: false,
    ratings: false,
    misc: false,
  };

  public readonly periodOptions: PeriodOptions = {
    performance: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10, Period.YEAR_15, Period.SINCE_INCEPTION],
    maximum_draw_down: [Period.YEAR_5, Period.SINCE_INCEPTION],
    volatility: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10],
    sharpe_ratio: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10],
    tracking_error: [Period.YEAR_1, Period.YEAR_5, Period.YEAR_10],
    correlation_coefficient: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10],
    information_ratio: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5],
    beta: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10],
    treynor_ratio: [Period.YEAR_1, Period.YEAR_3, Period.YEAR_5, Period.YEAR_10],
  };

  private lastSearchState: Nullable<SearchParameters> = null;
  private currentSearchWasTracked = false;
  private debouncedSearch = debounce(Constants.defaultDelay, this.search);

  public get adIds(): { header: string; skyscraper: string; sidebar_2nd: string } {
    return {
      header: ADS_INVENTORY.product_search_header_slot.dom_id,
      skyscraper: ADS_INVENTORY.product_search_sidebar_top.dom_id,
      sidebar_2nd: ADS_INVENTORY.product_search_sidebar_2nd.dom_id,
    };
  }

  public get currentSortOption(): Products.PerformanceSortingOption {
    return this.sortingOptions.find((option) => option.value === this.lastSearchState?.index) || this.sortingOptions[0];
  }
  // eslint-disable-next-line no-empty-function
  public set currentSortOption(_val: Products.PerformanceSortingOption) {}

  public get sortingOptions(): Products.PerformanceSortingOption[] {
    return [
      {
        value: `${this.rawIndexName}_perf_1y_desc`,
        label: this.$t("ui.shared.global.metrics.performance.long_with_year_period", {
          period: 1,
        }) as string,
        performance_key: "performance_1_year",
        performance_table_header: this.$t("ui.shared.global.metrics.performance.abbr_with_year_period", {
          period: 1,
        }) as string,
      },
      {
        value: `${this.rawIndexName}_perf_3y_desc`,
        label: this.$t("ui.shared.global.metrics.performance.long_with_year_period", {
          period: 3,
        }) as string,
        performance_key: "performance_3_years",
        performance_table_header: this.$t("ui.shared.global.metrics.performance.abbr_with_year_period", {
          period: 3,
        }) as string,
      },
      {
        value: `${this.rawIndexName}_perf_5y_desc`,
        label: this.$t("ui.shared.global.metrics.performance.long_with_year_period", {
          period: 5,
        }) as string,
        performance_key: "performance_5_years",
        performance_table_header: this.$t("ui.shared.global.metrics.performance.abbr_with_year_period", {
          period: 5,
        }) as string,
      },
    ];
  }

  public get showReferenceTab(): boolean {
    return this.showAllFilters && !this.resultsInViewport;
  }

  public get query(): string {
    return this.lastSearchState?.query || "";
  }

  public get focusAsset(): Nullable<string> {
    const focusAssetValues = this.lastSearchState?.hierarchicalFacetsRefinements.focus_asset || [null];
    return focusAssetValues[0];
  }

  public get focusRegion(): Nullable<string> {
    const focusRegionValues = this.lastSearchState?.hierarchicalFacetsRefinements.focus_region || [null];
    return focusRegionValues[0];
  }

  public get tranche(): Nullable<string> {
    const trancheValues = this.lastSearchState?.hierarchicalFacetsRefinements.tranche || [null];
    return trancheValues[0];
  }

  public get currency(): Nullable<string> {
    const currencyValues = this.lastSearchState?.hierarchicalFacetsRefinements.currency || [null];
    return currencyValues[0];
  }

  public get fundCompany(): Nullable<string> {
    const fundCompanyValues = this.lastSearchState?.hierarchicalFacetsRefinements.fund_company || [null];
    return fundCompanyValues[0];
  }

  public get fields(): string[][] {
    return [
      ["performance", this.currentSortOption.performance_table_header],
      ["volatility", this.$t("ui.shared.products.table.heads.volatility") as string],
      ["price", this.$t("ui.shared.products.table.heads.price") as string],
      ["max_drawdown", this.$t("ui.shared.products.table.heads.max_draw_down") as string],
      ["sri", this.$t("ui.shared.products.table.heads.sri") as string],
    ];
  }

  public scrollToResults(): void {
    const results = this.$refs.results as HTMLElement;
    if (results) results.scrollIntoView();
  }

  public toggleFilters(): void {
    this.showAllFilters = !this.showAllFilters;
    window.scrollTo(window.scrollX, window.scrollY + 1);
  }

  public onPaginationChange(): void {
    setTimeout(() => {
      this.results.scrollIntoView();
    }, 100);
  }

  public clearAllRefinements(): void {
    Emitter.emit("ci::clear-refinement::ais");
  }

  public searchFunction(helper: AlgoliaSearchHelper): void {
    /*
     * debounce search. If we don't do this instantsearch will start a query for each refinement we have. So around
     * 15 per page load. This will also prevent us from starting a search for every keystroke for our query term.
     */
    this.debouncedSearch(helper);
  }

  public search(helper: AlgoliaSearchHelper): void {
    if (this.searchFiltersChanged(helper.state)) {
      this.currentSearchWasTracked = false;
      ciTrackingService.resetImpressions();
    }

    this.lastSearchState = helper.state;
    helper.search();
  }

  public handlePerformancePeriodChange(performance: string, refine: (index: string) => void): void {
    const option = this.sortingOptions.find((item) => item.performance_key === performance) || this.sortingOptions[0];

    refine(option.value);
  }

  public getRefinementLabelTranslation(
    item: { label: string; refinements: { label: string }[] },
    prefix: false,
    index = 0
  ): string {
    const { label } = item;
    const periodRegex = /(_\d{1,2}_.+)|(_ytd)|(_since_inception)/;
    const withPeriod = RegExp(periodRegex).test(label);
    if (withPeriod) {
      const splitted = label.split(periodRegex).filter((x: string) => x);
      return `${this.$t("ui.views.products.fundsearch.header.filters." + splitted[0])} ${this.$t(
        "products.periods." + splitted[1]
      )}`;
    } else {
      if (label === "sustainable_preference") {
        if (prefix) return this.$t("ui.shared.global.models.products.sfdr") as string;
        else {
          const article = item.refinements[index].label;
          return `${this.$t("ui.views.products.fundsearch.header.filters." + label + "." + article)}`;
        }
      }
      return `${this.$t("ui.views.products.fundsearch.header.filters." + label)}`;
    }
  }

  public labelFor(refinement: { attribute: string; label: string }): string {
    if (["minimum_initial_investment_range", "volume_range", "age_range", "tranche"].includes(refinement.attribute)) {
      return this.translateSelectItem(refinement);
    }

    return refinement.label;
  }

  public mapProducts(products: Products.Algolia[]): Products.TableData[] {
    return products.map((product) => ({
      ...product,
      price_last_updated_at: new Date((product.price_last_updated_at || product.updated_at) * 1000).toISOString(),
      performance: product.performance[this.currentSortOption.performance_key],
      performance_1_year: product.performance.performance_1_year,
      performance_3_years: product.performance.performance_3_years,
      performance_5_years: product.performance.performance_5_years,
      volatility: product.ratios_euro?.volatility_1_year,
      max_drawdown: product.ratios_fund_currency?.maximum_draw_down_since_inception,
      peergroup: product.peergroup_name,
    }));
  }

  public aisSelectOptionsMapper(option: AisSelectOptionPayload): ExplicitAny {
    return {
      ...option,
      key: option.value,
      label: `${option.label} (${option.count})`,
    };
  }

  public sortAndTranslateSelectItems(items: string[]): string[] {
    return this.translateSelectItems(items.sort());
  }

  public translateSelectItems(items: any[]): any[] {
    return items.map((i) => ({ ...i, label: this.translateSelectItem(i) }));
  }

  public translateSelectItem(item: any[string]): any {
    return this.$t("ui.views.products.fundsearch.header.filters." + item.label);
  }

  public async beforeRouteEnter(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
    const loader = useLoader();
    loader.start();

    next(async (vm: any) => {
      try {
        if (to.query.sortBy) {
          vm.currentSortOption =
            vm.sortingOptions.find((option: Products.PerformanceSortingOption) => option.value === to.query.sortBy) ||
            vm.sortingOptions[0];
        }

        await ciTrackingService.trackView("FundSearch");
      } catch (error) {
        apiService.handleAPIError(error);
      } finally {
        loader.stop();
      }
    });
  }

  public trackComparisonStarted(): void {
    gtmTrackingService.track({
      eventCategory: "Product",
      eventAction: "ProductComparisonOnFundSearch",
    });

    this.trackQuery();
  }

  public trackClick(clickedProduct: Nullable<Products.Legacy.Product>): void {
    this.trackQuery();
    this.gtmTracking(clickedProduct);
  }

  protected mounted(): void {
    Emitter.on("ci::element-in-viewport", (type: string) => type === "result_table" && (this.resultsInViewport = true));
    Emitter.on(
      "ci::element-out-viewport",
      (type: string) => type === "result_table" && (this.resultsInViewport = false)
    );
    if (!this.$user.signedIn) setTimeout(() => this.$bvModal.show("modal-registration-soft-gating"), 20000);
  }

  protected destroyed(): void {
    Emitter.off("ci::element-in-viewport");
    Emitter.off("ci::element-out-viewport");
  }

  private gtmTracking(clickedProduct: Nullable<Products.Legacy.Product>): void {
    let eventAction = "SearchByProductOnFundSearch";

    if (this.focusAsset) {
      eventAction = this.query ? "SearchByAssetfocusSearchByProductOnFundSearch" : "SearchByAssetfocusOnFundSearch";
    }

    gtmTrackingService.track({
      eventCategory: "Product",
      eventAction,
      eventLabel: {
        product_id: clickedProduct?.id.toString() || null,
        asset_focus: this.focusAsset,
        asset_region: this.focusRegion,
        asset_manager: this.fundCompany,
        currency: this.currency,
        query: this.query,
        tranche: this.tranche,
      },
    });
  }

  private trackQuery(): void {
    if (this.currentSearchWasTracked) return;

    this.currentSearchWasTracked = true;

    ciTrackingService.track({
      event: { action: "search", value: null },
      target: { name: "Search", identifier: this.query || "<No Query>" },
      source: { name: "Fundsearch", identifier: window.location.href },
    });
  }

  private searchFiltersChanged(state: SearchParameters): boolean {
    if (!this.lastSearchState) {
      return true;
    }

    return !this.searchFiltersEqual(state, this.lastSearchState);
  }

  private searchFiltersEqual(state: any, otherState: any): boolean {
    let differenceFound = false;

    Object.keys(state).forEach((keyName) => {
      if (differenceFound) return;

      const key = keyName as keyof SearchParameters;
      if (key === "page") return;

      if (Array.isArray(state[key])) {
        differenceFound = state && otherState && state[key]?.length !== otherState[key]?.length;
      } else if (typeof state[key] === "object") {
        differenceFound = !this.searchFiltersEqual(state[key], otherState[key]);
      } else {
        differenceFound = state[key] !== otherState![key];
      }
    });

    return !differenceFound;
  }
}
